import React, { useEffect, useMemo, useState } from 'react'
import { Box } from '@mui/material'
import {
  Window,
  MessageList,
  MessageInput,
  useChannelStateContext,
  MessageInputFlat,
  useMessageContext,
  MessageSimple,
  useChannelActionContext,
  useMessageComposer,
  Attachment,
  UnsupportedAttachment,
  PinIndicator as StreamPinIndicator,
  useTranslationContext,
} from 'stream-chat-react'
import ChannelHeader from './channel-header'
import { messagingRenderText } from './messaging-render-text'
import { shouldSuppressCardUrlMessageText } from './messaging-utils'
import useQueryString from 'lib/hooks/use-query-string'
import { useMediaInfoFactory } from 'lib/hooks/use-media-info-factory'
import { useVideoAnalysis } from 'lib/hooks/use-video-analysis'
import {
  GroupEventAttachmentCard,
  LessonTypeAttachmentCard,
} from './components'
import {
  OUR_TYPES,
  prepareCustomAttachment,
  replaceMentions,
  fileUploadRequestOverride,
} from './utils'
import { useAttachmentContext } from './use-attachment-context'
import { useFollowUpSent } from './follow-up-sent-context'
import PlaceholderWithQrCode from './components/placeholder-with-qr-code'
import MessagingFileAttachment from './messaging-file-attachment'
import MessagingLinkAttachmentCard from './messaging-link-attachment-card'
import MessagingAudio from './messaging-audio'
import MessagingVideoPlayer from './messaging-video-player'
import MessagingVoiceRecording from './messaging-voice-recording'
import CampaignMessageLabel from './campaign-message-label'
import { useAuth } from 'lib/auth'
import { flags, useFeatureFlags } from 'lib/feature-flags'
import PromotionsProvider from './promotions/promotions-context'
import PromotionsStrip from './promotions/promotions-strip'
import PromotionsOffNotice from './promotions/promotions-off-notice'
import ChatFollowUpHelper, {
  FOLLOW_UP_CUSTOM_FIELD,
  FOLLOW_UP_PARAM,
  followUpTag,
} from 'components/student-insights/chat-follow-up-helper'

// MessageSimple calls PinIndicator with no props, so we pull message + t from context.
export const CustomPinIndicator = () => {
  const { message } = useMessageContext()
  const { t: translate } = useTranslationContext()
  if (!message.pinned) return null
  return <StreamPinIndicator message={message} t={translate} />
}

/*
 * In stream-chat-react v13, custom attachment types are no longer routed through
 * CardContainer. Instead, anything that isn't a known built-in type (scraped URL
 * preview, video, audio, file, etc.) falls through to UnsupportedAttachmentContainer.
 * We override UnsupportedAttachment to handle our custom types. Note the API change:
 * the override receives { attachment } (the whole object) rather than spread props.
 */
const CustomUnsupportedAttachment = ({ attachment }) => {
  switch (attachment?.type) {
    case 'pga_group_event':
      return <GroupEventAttachmentCard {...attachment} />
    case 'pga_lesson_type':
      return <LessonTypeAttachmentCard {...attachment} />
    case 'pga_student_library_upload':
      // This is a stopgap effort. In the future, we may instead support displaying the video.
      return <PlaceholderWithQrCode />
    default:
      return <UnsupportedAttachment attachment={attachment} />
  }
}

export const CustomAttachment = props => {
  return (
    <Attachment
      {...props}
      Audio={MessagingAudio}
      Card={MessagingLinkAttachmentCard}
      File={MessagingFileAttachment}
      Media={MessagingVideoPlayer}
      UnsupportedAttachment={CustomUnsupportedAttachment}
      VoiceRecording={MessagingVoiceRecording}
    />
  )
}

export const CustomMessage = () => {
  let { message } = useMessageContext()
  const { user } = useAuth()
  const mediaInfoFactory = useMediaInfoFactory()
  const attachments = message.attachments ?? []
  const isMyMessage = message.user?.id === user?.externalId
  const nonCompliantVideos = useVideoAnalysis(
    attachments,
    mediaInfoFactory,
    !isMyMessage,
  )
  // Listed Group Events may be scraped and generate a URL preview. This causes TWO preview cards to appear.
  // When one of our custom attachments is present, drop the scraped previews and keep everything else
  // (campaign messages can carry several offering cards plus images and files).
  const hasCustomAttachment = attachments.some(att =>
    OUR_TYPES.includes(att.type),
  )
  if (hasCustomAttachment && attachments.some(att => att.og_scrape_url)) {
    message.attachments = attachments.filter(att => !att.og_scrape_url)
  }

  if ((message.mentioned_users?.length ?? 0) > 0) {
    message = replaceMentions({ message: message })
  }

  if (shouldSuppressCardUrlMessageText(message)) {
    message = { ...message, text: '' }
  }

  return (
    <>
      {nonCompliantVideos.length > 0 && (
        <Box maxWidth="375px">
          <PlaceholderWithQrCode type="unsupported-video" />
        </Box>
      )}
      {isMyMessage && message.coach_campaign_id ? (
        <CampaignMessageLabel />
      ) : null}
      <MessageSimple message={message} />
    </>
  )
}

const UserMessageInput = () => {
  const messageComposer = useMessageComposer()
  const { shared } = useQueryString()
  const prompt = 'Check out this link for booking info: '

  useEffect(() => {
    if (shared) {
      messageComposer?.textComposer?.setText(prompt + shared)
    }
  }, [shared, messageComposer])

  return <MessageInputFlat />
}

// Default message actions minus 'quote' (which renders as "Reply" and creates a
// quoted reply in the input). We replace it with a custom action that opens the
// thread panel instead, making "Reply" behave like "Open Thread".
const DEFAULT_ACTIONS_WITHOUT_QUOTE = [
  'delete',
  'edit',
  'flag',
  'markUnread',
  'mute',
  'pin',
  'react',
  'reply',
]

// The contact in a coach's 1:1 conversation, for Student Insights follow-up help.
export const followUpContactId = (channel, user) => {
  const members = Object.values(channel?.state?.members || {})
  if (members.length !== 2) return null
  const contact = members.find(
    member =>
      member.user?.id !== user?.externalId && member.user?.type === 'Contact',
  )
  return contact?.user?.id || null
}

export const ChannelInner = () => {
  const { sendMessage, openThread } = useChannelActionContext()
  const { shared, attType, id } = useAttachmentContext()
  const { channel } = useChannelStateContext()
  const messageComposer = useMessageComposer()
  const { user, isCoach } = useAuth()
  const [isStudentInsightsEnabled] = useFeatureFlags([
    flags.FLAG_FEAT_STUDENT_INSIGHTS,
  ])
  const { [FOLLOW_UP_PARAM]: requestedStudentId } = useQueryString()
  const [followUp, setFollowUp] = useState(null)
  const { sentStudentIds, markSent } = useFollowUpSent()
  const followUpContactExternalId =
    isCoach && isStudentInsightsEnabled && !user?.hideSensitiveInformation
      ? followUpContactId(channel, user)
      : null

  const customMessageActions = useMemo(
    () => ({
      Reply: (message, event) => openThread(message, event),
    }),
    [openThread],
  )

  // While assuming another user, never let the impersonating admin mutate the
  // user's read state — strip the "Mark Unread" per-message action since it
  // calls channel.markUnread directly and bypasses doMarkReadRequest.
  const messageActions = useMemo(
    () =>
      user?.isAssumed
        ? DEFAULT_ACTIONS_WITHOUT_QUOTE.filter(a => a !== 'markUnread')
        : DEFAULT_ACTIONS_WITHOUT_QUOTE,
    [user?.isAssumed],
  )

  useEffect(() => {
    const customUpload = async file => {
      return fileUploadRequestOverride(file, channel)
    }
    messageComposer?.attachmentManager?.setCustomUploadFn(customUpload)
  }, [channel, messageComposer])

  const overrideSubmitHandler = async ({
    localMessage,
    message,
    sendOptions,
  }) => {
    const customAttachment = prepareCustomAttachment({
      message: localMessage,
      shared: shared,
      attType: attType,
      id: id,
    })
    if (customAttachment) {
      localMessage.attachments = [
        ...(localMessage.attachments || []),
        customAttachment,
      ]
    }

    // Admins assuming a coach can see the helper but never log follow-ups.
    const tag =
      followUp && !user?.isAssumed ? followUpTag(followUp, message.text) : null
    if (tag) {
      message[FOLLOW_UP_CUSTOM_FIELD] = tag
      localMessage[FOLLOW_UP_CUSTOM_FIELD] = tag
    }

    if (sendMessage) {
      await sendMessage({ localMessage, message, options: sendOptions })
      if (tag) markSent(tag.student_id)
    }
  }

  return (
    <PromotionsProvider channel={channel}>
      <Window>
        <ChannelHeader />
        <PromotionsStrip />
        <MessageList
          style={{ textAlign: 'left !important' }}
          messageActions={messageActions}
          customMessageActions={customMessageActions}
          renderText={messagingRenderText}
        />
        <PromotionsOffNotice />
        {followUpContactExternalId ? (
          <ChatFollowUpHelper
            key={followUpContactExternalId}
            contactExternalId={followUpContactExternalId}
            requestedStudentId={requestedStudentId}
            sentStudentIds={sentStudentIds}
            onActiveChange={setFollowUp}
          />
        ) : null}
        <MessageInput
          focus={false}
          Input={UserMessageInput}
          overrideSubmitHandler={overrideSubmitHandler}
          maxRows={12}
        />
      </Window>
    </PromotionsProvider>
  )
}
